import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Contact, ContactsResponse, ContactType } from "../types";

interface UseContactsParams {
  search?: string;
  contactType?: ContactType | "all";
}

interface UseContactsResult {
  contacts: Contact[];
  isLoading: boolean;
  hasLoaded: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useContacts(params: UseContactsParams = {}): UseContactsResult {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchContacts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.set("search", params.search);
        if (params.contactType && params.contactType !== "all") {
          queryParams.set("contactType", params.contactType);
        }

        const queryString = queryParams.toString();
        const url = `/api/contacts${queryString ? `?${queryString}` : ""}`;
        const data = await apiFetch.get<ContactsResponse>(url);

        setContacts(data.contacts);
      } catch (err) {
        setError(toError(err));
        setContacts([]);
      } finally {
        setHasLoaded(true);
        setIsLoading(false);
      }
    };

    fetchContacts();
  }, [fetchKey, params.search, params.contactType]);

  const refetch = useCallback(() => setFetchKey((key) => key + 1), []);

  return { contacts, isLoading, hasLoaded, error, refetch };
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) return error;
  return error instanceof Error ? error : new Error("Unknown error");
}
